import React, { useState, useEffect } from 'react';
import { Star, CheckCircle2 } from 'lucide-react';

const TICKER_ITEMS = [
  'ACME CORP', 'GLOBEX INDUSTRIES', 'INITECH SOLUTIONS', 'UMBRELLA CO',
  'STARK VENTURES', 'WAYNE ENTERPRISES', 'MASSIVE DYNAMIC', 'VERIDIAN DYNAMICS',
  'OSCORP GROUP', 'SOYLENT CORP',
];

const FAQ_ITEMS = [
  {
    q: 'What is AI Visibility and why does it matter now?',
    a: 'AI Visibility measures how well ChatGPT, Gemini, Claude, and Perplexity know and recommend your brand. As AI becomes the primary research tool for millions, being cited in AI responses is now as critical as a Google Page 1 ranking — and most brands are invisible.',
  },
  {
    q: "What's the difference between SEO, AEO, and GEO?",
    a: 'SEO covers traditional search rankings. AEO (Answer Engine Optimization) targets featured snippets and answer boxes. GEO (Generative Engine Optimization) ensures your brand appears in AI-generated summaries. All three are essential for modern visibility.',
  },
  {
    q: "Is the audit really free? What's the catch?",
    a: 'Completely free — no credit card, no email required. We offer paid plans for teams needing continuous monitoring and competitor tracking, but the core report is always free.',
  },
  {
    q: 'How accurate are the visibility scores?',
    a: 'We query major AI platforms directly and measure real responses. Combined with live crawl data, our scores are validated at 98% accuracy — not estimates or proxies.',
  },
  {
    q: 'How do I improve my AI Visibility score?',
    a: 'Your personalized report includes a ranked action plan. High-impact moves include authoritative content, industry citations, structured data markup, and building your Wikipedia and knowledge graph presence.',
  },
];

export default function LandingSections({ onCtaClick }) {
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const els = document.querySelectorAll('.pa-rv:not(.in)');
    const io = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('in');
          io.unobserve(e.target);
        }
      }),
      { threshold: 0.1 }
    );
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  const tickerDoubled = [...TICKER_ITEMS, ...TICKER_ITEMS];

  return (
    <>
      {/* Ticker */}
      <div className="pa-ticker">
        <div className="pa-tf">
          <div className="pa-tt">
            {tickerDoubled.map((name, i) => (
              <div key={i} className="pa-ti2">
                <div className="pa-td" />
                {name}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* How it works */}
      <section className="pa-sec pa-how" id="how">
        <div className="pa-hg">
          <div>
            <div className="pa-ew pa-rv">How It Works</div>
            <h2 className="pa-sh2 pa-rv pa-d1">Four steps to <em>total visibility</em></h2>
            <p className="pa-sp pa-rv pa-d2">From zero to actionable intelligence in under 2 minutes. No technical expertise required.</p>
            <div className="pa-hs">
              {[
                ['01', 'Enter your company', 'Add your company name and website URL. No sign-up, no credit card, no friction.'],
                ['02', 'We crawl & analyze', 'Our AI scans across search engines, answer engines, and all major AI platforms simultaneously.'],
                ['03', 'Get your scores', 'Detailed SEO, AEO, GEO, and AI Visibility scores with insights ranked by impact.'],
                ['04', 'Download your report', "A comprehensive PDF with exact, prioritized steps tailored to your brand's gaps."],
              ].map(([n, t, d], i) => (
                <div key={n} className={`pa-hst pa-rv pa-d${i + 1}`}>
                  <div className="pa-hn">{n}</div>
                  <div>
                    <div className="pa-ht">{t}</div>
                    <div className="pa-hd">{d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="pa-hm pa-rv pa-d2">
            <div className="pa-mb">
              <div className="pa-md" style={{ background: '#FF5F57' }} />
              <div className="pa-md" style={{ background: '#FEBC2E' }} />
              <div className="pa-md" style={{ background: '#28C840' }} />
              <div className="pa-mt">pr_audit · live_scan.json</div>
            </div>
            <div className="pa-mbody">
              <div className="pa-msr">
                <div className="pa-rl">
                  <svg width="104" height="104" viewBox="0 0 104 104">
                    <circle cx="52" cy="52" r="42" fill="none" stroke="var(--bg3)" strokeWidth="7" />
                    <circle cx="52" cy="52" r="42" fill="none" stroke="url(#pa-rg)" strokeWidth="7" strokeDasharray="263.9" strokeDashoffset="66" strokeLinecap="round" transform="rotate(-90 52 52)" />
                    <defs>
                      <linearGradient id="pa-rg" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#3D1FCC" />
                        <stop offset="100%" stopColor="#D42F5C" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="pa-rc2">
                    <div className="pa-rnum">75</div>
                    <div className="pa-rsub">Score</div>
                  </div>
                </div>
                <div>
                  <div className="pa-mco">Acme Corporation</div>
                  <div className="pa-murl">acmecorp.com · Scanned now</div>
                  <div className="pa-mvd">
                    <CheckCircle2 size={12} style={{ flexShrink: 0 }} />
                    Strong visibility · 10 actions
                  </div>
                </div>
              </div>
              <div className="pa-mbrs">
                {[
                  ['SEO', 85, 'linear-gradient(90deg,var(--teal),var(--teal2))'],
                  ['AEO', 68, 'linear-gradient(90deg,var(--saffron),var(--saffron2))'],
                  ['GEO', 62, 'linear-gradient(90deg,var(--rose),var(--rose2))'],
                  ['AI', 75, 'linear-gradient(90deg,var(--indigo),var(--indigo3))'],
                ].map(([k, v, bg]) => (
                  <div key={k} className="pa-mbr">
                    <div className="pa-mbk">{k}</div>
                    <div className="pa-mbt"><div className="pa-mbf" style={{ width: `${v}%`, background: bg }} /></div>
                    <div className="pa-mbv">{v}</div>
                  </div>
                ))}
              </div>
              <div className="pa-ag">
                {[
                  ['GPT', '72%', 'ChatGPT', 'rgba(16,163,127,.12)', '#10A37F'],
                  ['GEM', '78%', 'Gemini', 'rgba(66,133,244,.12)', '#4285F4'],
                  ['PPX', '61%', 'Perplexity', 'var(--indigo-mid)', 'var(--indigo)'],
                ].map(([abbr, pct, name, bg, color]) => (
                  <div key={abbr} className="pa-ac">
                    <div className="pa-al" style={{ background: bg, color }}>{abbr}</div>
                    <div className="pa-ap2">{pct}</div>
                    <div className="pa-an">{name}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="pa-sec pa-pb" id="features">
        <div className="pa-ph">
          <div className="pa-ew pa-rv">Comprehensive Analysis</div>
          <h2 className="pa-sh2 pa-rv pa-d1">Four pillars of <em>modern visibility</em></h2>
          <p className="pa-sp pa-rv pa-d2">Traditional SEO alone is no longer enough. Brands need visibility across AI, search, answers, and generative engines simultaneously.</p>
        </div>
        <div className="pa-pg">
          {[
            { cls: 'pa-pl1', tag: 'AI Visibility', score: 72, title: 'AI Visibility Score', desc: 'Measure how well ChatGPT, Gemini, Claude, and Perplexity know your brand. The new frontier of discovery — most companies are completely invisible.', chips: ['ChatGPT mentions', 'Gemini visibility', 'Perplexity ranking'], bars: [['ChatGPT', 72], ['Gemini', 64]] },
            { cls: 'pa-pl2', tag: 'SEO', score: 85, title: 'SEO Analysis', desc: 'Traditional search engine optimization still drives most organic traffic. Rankings, backlink profile, and technical health analyzed with precision instruments.', chips: ['Keyword rankings', 'Backlink profile', 'Core Web Vitals'], bars: [['On-page', 85], ['Authority', 68]] },
            { cls: 'pa-pl3', tag: 'AEO', score: 64, title: 'AEO Assessment', desc: 'Answer Engine Optimization determines if you appear in featured snippets and answer boxes. Voice search is growing — you need to be heard clearly.', chips: ['Featured snippets', 'Answer boxes', 'Schema markup'], bars: [['Snippets', 64], ['Voice', 48]] },
            { cls: 'pa-pl4', tag: 'GEO', score: 58, title: 'GEO Evaluation', desc: 'Generative Engine Optimization measures your presence in AI-generated summaries and responses. The future of discovery — are you in it?', chips: ['AI summaries', 'Citation tracking', 'Content authority'], bars: [['Citations', 58], ['Authority', 42]] },
          ].map((p, i) => (
            <div key={p.tag} className={`pa-pl ${p.cls} pa-rv ${i ? `pa-d${i}` : ''}`}>
              <div className="pa-ptop">
                <div className="pa-ptag">{p.tag}</div>
                <div className="pa-psc">{p.score}</div>
              </div>
              <div className="pa-ptit">{p.title}</div>
              <p className="pa-pdesc">{p.desc}</p>
              <div className="pa-pchips">
                {p.chips.map(c => <span key={c} className="pa-pchip">{c}</span>)}
              </div>
              <div className="pa-pbrs">
                {p.bars.map(([l, w]) => (
                  <div key={l} className="pa-pbr">
                    <div className="pa-pbl">{l}</div>
                    <div className="pa-pbt"><div className="pa-pbf" style={{ width: `${w}%` }} /></div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Report */}
      <section className="pa-sec pa-rpt">
        <div className="pa-rg">
          <div>
            <div className="pa-ew pa-rv">Actionable Reports</div>
            <h2 className="pa-sh2 pa-rv pa-d1">Not just scores.<br /><em>Precision actions.</em></h2>
            <p className="pa-sp pa-rv pa-d2">Every audit produces a downloadable PDF with specific, prioritized steps tailored to your brand. No generic templates. Real intelligence.</p>
            <ul className="pa-rcl pa-rv pa-d3">
              {[
                'Full-spectrum analysis across all 4 visibility metrics',
                'Competitive benchmarking against your specific industry',
                'Prioritized action items with estimated impact scores',
                'Downloadable PDF report for your entire team',
                'Re-audit in 30 days to track improvement',
              ].map(text => (
                <li key={text}>
                  <div className="pa-rck">
                    <svg width="11" height="11" fill="none" viewBox="0 0 24 24" stroke="var(--indigo)" strokeWidth="2.5"><path d="M5 13l4 4L19 7" /></svg>
                  </div>
                  {text}
                </li>
              ))}
            </ul>
            <button type="button" className="pa-rbtn pa-rv pa-d4" onClick={onCtaClick}>
              Get Free Report
              <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
            </button>
          </div>
          <div className="pa-rv pa-d2">
            <div className="pa-pdfcard">
              <div className="pa-pdfbar">
                <div className="pa-pdfdt" style={{ background: '#FF5F57' }} />
                <div className="pa-pdfdt" style={{ background: '#FEBC2E' }} />
                <div className="pa-pdfdt" style={{ background: '#28C840' }} />
                <div className="pa-pdft">pr_audit_report_acmecorp.pdf</div>
              </div>
              <div className="pa-pdfb">
                <div className="pa-pdfsg">
                  {[
                    ['pa-psi', 72, 'AI Visibility'],
                    ['pa-pss', 85, 'SEO Score'],
                    ['pa-psa', 64, 'AEO Score'],
                    ['pa-psg', 58, 'GEO Score'],
                  ].map(([cls, n, label]) => (
                    <div key={label} className={`pa-pdfsc ${cls}`}>
                      <div className="pa-psn">{n}</div>
                      <div>
                        <div className="pa-psl">{label}</div>
                        <div className="pa-psv">{n} / 100</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="pa-pdfah">// action_items · sorted by impact</div>
                <div className="pa-pdfas">
                  {[
                    'Add structured FAQ schema to all service pages',
                    'Publish authoritative category content bi-weekly',
                    'Secure 8+ citations from high-DA publications',
                    'Build Wikipedia presence and knowledge graph data',
                  ].map((text, i) => (
                    <div key={i} className="pa-pdfa">
                      <div className="pa-pdfan">{i + 1}</div>
                      {text}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="pa-sec pa-testibg" id="stories">
        <div style={{ textAlign: 'center', marginBottom: 54 }}>
          <div className="pa-ew pa-rv" style={{ justifyContent: 'center' }}>
            <span style={{ display: 'block', width: 26, height: 1, background: 'var(--indigo)' }} />
            Customer Stories
          </div>
          <h2 className="pa-sh2 pa-rv pa-d1" style={{ margin: '0 auto' }}>What teams are saying</h2>
        </div>
        <div className="pa-tg">
          {[
            { q: '"We had no idea ChatGPT never mentioned us. Three months after following the report\'s actions, we appear in 68% of relevant AI queries. This platform is essential."', initials: 'SC', name: 'Sarah Chen', role: 'Head of Growth · TechCorp', grad: 'linear-gradient(135deg,var(--indigo),var(--rose))' },
            { q: '"The GEO score opened our eyes. We were invisible in AI-generated summaries while competitors were cited everywhere. The action items were specific and fast to implement."', initials: 'MR', name: 'Marcus Rodriguez', role: 'CMO · Innovate Labs', grad: 'linear-gradient(135deg,var(--teal),var(--indigo))' },
            { q: '"Ran an audit Monday, implemented the top 3 actions by Wednesday. AEO score jumped 19 points in 30 days. The free tier is genuinely useful — not a bait-and-switch."', initials: 'AP', name: 'Aisha Patel', role: 'Founder · GrowthCo', grad: 'linear-gradient(135deg,var(--saffron),var(--rose))' },
          ].map((t, i) => (
            <div key={t.initials} className={`pa-tc pa-rv ${i ? `pa-d${i}` : ''}`}>
              <div className="pa-ts">
                {[...Array(5)].map((_, j) => (
                  <Star key={j} size={13} fill="var(--saffron)" color="var(--saffron)" className="pa-star" />
                ))}
              </div>
              <div className="pa-tq">{t.q}</div>
              <div className="pa-ta">
                <div className="pa-tav" style={{ background: t.grad }}>{t.initials}</div>
                <div>
                  <div className="pa-tn">{t.name}</div>
                  <div className="pa-tr2">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="pa-sec pa-faqbg" id="faq">
        <div className="pa-faqg">
          <div>
            <div className="pa-ew pa-rv">FAQ</div>
            <h2 className="pa-sh2 pa-rv pa-d1">Common<br /><em>questions</em></h2>
            <p className="pa-sp pa-rv pa-d2" style={{ marginBottom: 0 }}>Everything you need to know about AI visibility and how our platform works.</p>
          </div>
          <div className="pa-faql">
            {FAQ_ITEMS.map((item, i) => (
              <div key={i} className={`pa-faqi ${openFaq === i ? 'open' : ''}`}>
                <button type="button" className="pa-faqq" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  {item.q}
                  <div className="pa-faqic">
                    <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
                  </div>
                </button>
                <div className="pa-faqa">{item.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pa-ctasec">
        <div className="pa-cb1" />
        <div className="pa-cb2" />
        <div className="pa-cb3" />
        <div className="pa-ew pa-rv" style={{ justifyContent: 'center', marginBottom: 18, position: 'relative' }}>
          <span style={{ display: 'block', width: 26, height: 1, background: 'var(--indigo)' }} />
          Start now · it's free
        </div>
        <h2 className="pa-ctah pa-rv pa-d1">Ready to see how<br /><em>AI sees you?</em></h2>
        <p className="pa-ctap pa-rv pa-d2">Join thousands of brands who've discovered their AI visibility score. Free, instant, no sign-up.</p>
        <div className="pa-rv pa-d3">
          <button type="button" className="pa-ctabtn" onClick={onCtaClick}>
            Run Free Audit Now
            <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
          </button>
        </div>
        <div className="pa-ctat pa-rv pa-d4">
          {['Instant results', 'Zero signup', '10,000+ brands audited', 'Free PDF report'].map(t => (
            <div key={t} className="pa-ctati">
              <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="var(--indigo)" strokeWidth="2.5"><path d="M5 13l4 4L19 7" /></svg>
              {t}
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
